/**
 * One drawable box on the calendar grid.
 *
 * Kind-coded on purpose: the three plan objects and logged time "never merge"
 * (the three-object taxonomy), so each kind gets its own colour family and
 * badge and there is no shared "event" styling to blur them. Positions are
 * the layout core's fractions — never pixels, never measured (jsdom renders
 * this file's output verbatim).
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { PositionedEntry } from './layoutTypes';
import type { ApiCalendarEntry } from './rangeTypes';

interface CalendarEntryBlockProps {
  positioned: PositionedEntry;
  entry: ApiCalendarEntry;
  onClick: (anchor: HTMLElement, entry: ApiCalendarEntry) => void;
}

const pct = (fraction: number): string => `${(fraction * 100).toFixed(3)}%`;

const CalendarEntryBlock: React.FC<CalendarEntryBlockProps> = ({
  positioned,
  entry,
  onClick,
}) => {
  const theme = useTheme();

  const palette: Record<ApiCalendarEntry['kind'], { main: string; label: string }> = {
    commitment_occurrence: { main: theme.palette.primary.main, label: 'Commitment' },
    planned_event: { main: theme.palette.grey[600], label: 'Imported' },
    task_block: { main: theme.palette.secondary.main, label: 'Task block' },
    time_entry: { main: theme.palette.success.main, label: 'Logged' },
  };
  const kindColor = palette[entry.kind].main;

  // Settled/displaced states draw muted so live plan reads as live plan.
  const muted =
    (entry.kind === 'task_block' &&
      (entry.state === 'PUSHED' || entry.state === 'RELEASED')) ||
    (entry.kind === 'commitment_occurrence' && entry.state === 'RESCHEDULED');

  const width = 100 / positioned.columnCount;
  const stateLabel =
    entry.kind === 'task_block' || entry.kind === 'commitment_occurrence'
      ? entry.state
      : null;

  const title = entry.title || (entry.kind === 'time_entry' ? entry.description : null);

  return (
    <Box
      component="button"
      type="button"
      data-entry-kind={entry.kind}
      data-entry-id={entry.id}
      data-segment={`${positioned.segmentIndex}/${positioned.segmentCount}`}
      data-live-edge={positioned.isLiveEdge ? 'true' : undefined}
      data-ghost={positioned.isGhost ? 'true' : undefined}
      data-zero-duration={positioned.isZeroDuration ? 'true' : undefined}
      data-clipped-start={positioned.clippedStart ? 'true' : undefined}
      data-clipped-end={positioned.clippedEnd ? 'true' : undefined}
      aria-label={`${palette[entry.kind].label}: ${title || 'untitled'}${
        positioned.isGhost
          ? ' (expected)'
          : positioned.isLiveEdge
            ? ' (running)'
            : ''
      }${positioned.isZeroDuration ? ' (zero duration)' : ''}`}
      onClick={(event) => onClick(event.currentTarget, entry)}
      sx={{
        position: 'absolute',
        top: pct(positioned.topFraction),
        height: pct(positioned.heightFraction),
        left: `${(positioned.column * width).toFixed(3)}%`,
        width: `${width.toFixed(3)}%`,
        boxSizing: 'border-box',
        textAlign: 'left',
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: 1,
        px: 0.5,
        py: 0.25,
        zIndex: positioned.isGhost ? 2 : 3,
        minHeight: positioned.isZeroDuration ? 14 : undefined,
        // Kind coding. Ghosts are intent, not fact: dotted and hollow.
        // Imported events are external truth: hatched/outlined, never solid.
        border: `1px ${
          positioned.isGhost
            ? 'dotted'
            : entry.kind === 'planned_event'
              ? 'dashed'
              : 'solid'
        } ${kindColor}`,
        borderBottom: positioned.isLiveEdge
          ? `3px solid ${kindColor}`
          : undefined,
        backgroundColor: positioned.isGhost
          ? 'transparent'
          : alpha(kindColor, muted ? 0.08 : entry.kind === 'planned_event' ? 0.12 : 0.24),
        opacity: muted ? 0.6 : 1,
        color: theme.palette.text.primary,
        fontFamily: 'inherit',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }} noWrap>
        {positioned.clippedStart ? '… ' : ''}
        {title || 'untitled'}
        {positioned.isGhost ? ' (expected)' : ''}
        {positioned.clippedEnd ? ' …' : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap display="block">
        {palette[entry.kind].label}
        {stateLabel ? ` · ${stateLabel}` : ''}
        {entry.kind === 'time_entry' && entry.running ? ' · running' : ''}
        {entry.kind === 'time_entry' && entry.overlap_acknowledged === true
          ? ' · overlap acknowledged'
          : ''}
      </Typography>
    </Box>
  );
};

export default CalendarEntryBlock;
